import { formatDateTime } from '@/features/application/utils/applicationFormatters'
import type { DefaultPaymentCompletedInfoCardProps } from '../types/components'

function approvalLabel(value: unknown): string {
  if (String(value ?? '') === '1') return 'Approved'
  if (String(value ?? '') === '0') return 'Not Approved'
  return '—'
}

function joinedName(first: unknown, last: unknown): string {
  return [first, last].filter(Boolean).join(' ').trim()
}

function actorDate(actor: unknown, date: unknown, withTime = false): string {
  if (!actor && !date) return '—'
  const normalizedDate =
    typeof date === 'string' && /^\d{10,13}$/.test(date)
      ? Number(date)
      : date
  const formatted = normalizedDate
    ? formatDateTime(normalizedDate as string | number | Date)
    : ''
  const datePart = withTime ? formatted : formatted.split(' ')[0]
  return [actor, datePart].filter(Boolean).join(' - ') || '—'
}

/** Completed DEFAULT payment audit card (Angular post-ACH/check save block). */
export function DefaultPaymentCompletedInfoCard({
  detail,
  hideActions = false,
  statementLoading = false,
  completing = false,
  onStatementClick,
  onCompleteClick,
}: DefaultPaymentCompletedInfoCardProps) {
  return (
    <section
      className="rounded-xl border border-sky-200 bg-sky-50 p-5 shadow-sm"
      data-testid="default-payment-completed-info"
    >
      <div className="space-y-4 text-sm text-slate-700">
        <p>
          <span className="font-semibold text-slate-600">Liberty Authorizer:</span>{' '}
          <span className="font-semibold">
            {approvalLabel(detail.liberty_authorization_approval)}
          </span>
        </p>
        <p>
          <span className="font-semibold text-slate-600">Created By:</span>{' '}
          <span className="font-semibold">
            {actorDate(
              joinedName(detail.payment_paid_first_name, detail.payment_paid_last_name),
              detail.payment_created_date,
              true,
            )}
          </span>
        </p>
        <p>
          <span className="font-semibold text-slate-600">Authorizer:</span>{' '}
          <span className="font-semibold">
            {actorDate(
              detail.liberty_authorization_user_name,
              detail.liberty_authorization_approval_date,
              true,
            )}
          </span>
        </p>
        <p>
          <span className="font-semibold text-slate-600">Payment Transaction Date:</span>{' '}
          <span className="font-semibold">
            {actorDate(detail.check_no_added_by, detail.check_no_added_date)}
          </span>
        </p>
        <p>
          <span className="font-semibold text-slate-600">Completed By:</span>{' '}
          <span className="font-semibold">
            {actorDate(detail.default_completed_by, detail.default_completed_date)}
          </span>
        </p>
      </div>
      {hideActions ? null : (
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onStatementClick}
            disabled={statementLoading || !detail.applicant_id || !detail.user_id}
            className="inline-flex min-h-11 flex-1 items-center justify-center rounded-xl bg-[rgb(21,151,255)] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgb(0,132,230)] disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none"
          >
            {statementLoading ? 'Loading...' : 'Reimbursement Settlement Statement'}
          </button>
          <button
            type="button"
            disabled={completing || !detail.default_statement}
            onClick={onCompleteClick}
            className="inline-flex min-h-11 flex-1 items-center justify-center rounded-xl bg-[rgb(21,151,255)] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgb(0,132,230)] disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none"
          >
            Complete Reimbursement
          </button>
        </div>
      )}
    </section>
  )
}
